import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, MessageSquare, MessageCircle, MapPin, Send, CheckCircle,
  Clock, ChevronDown, ChevronUp,
} from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55 } } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

const CONTACT_INFO = [
  {
    icon: Mail,
    label: 'Email Us',
    value: 'info@smartedunext.com',
    sub: 'We reply within 24 hours',
    color: 'text-accentBlue',
    bg: 'bg-accentBlue/10',
    border: 'border-accentBlue/20',
    href: 'mailto:info@smartedunext.com',
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: '077 186 6626',
    sub: 'Chat with us directly',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    href: 'https://wa.me/94771866626',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Colombo, Sri Lanka',
    sub: 'Serving students island-wide',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
    href: null,
  },
  {
    icon: Clock,
    label: 'Support Hours',
    value: 'Mon – Sat',
    sub: '8:00 AM – 8:00 PM',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
    href: null,
  },
];

const SUBJECTS = [
  'General Inquiry',
  'Resources & Past Papers',
  'Finding a Tutor',
  'Private / Online Classes',
  'Technical Support',
  'Other',
];

const FAQS = [
  {
    q: 'How do I find a tutor for my subject?',
    a: 'Navigate to "Find a Tutor" from the menu, then filter by syllabus, subject, and area. You can view tutor profiles and contact them directly.',
  },
  {
    q: 'Are the resources free to download?',
    a: 'Most past papers and notes are free. Some premium materials in the shop require purchase. Look for the "Premium" badge on resources.',
  },
  {
    q: 'How do I join an online class?',
    a: 'Log in as a student, then go to your syllabus page and click "Online Classes". You\'ll see upcoming sessions for your registered subjects with a Join link.',
  },
  {
    q: 'How do I register as a teacher?',
    a: 'Teacher accounts are created by the platform admin. Contact us via email or WhatsApp with your details and we\'ll set up your account.',
  },
];

// ── FAQ Item ──────────────────────────────────────────────────────────────────
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl transition-all duration-200 ${open ? 'border-accentBlue/30 bg-accentBlue/5' : 'border-white/10 bg-white/3 hover:border-white/20'}`}>
      <button onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left">
        <span className="font-semibold text-sm text-white">{q}</span>
        {open ? <ChevronUp size={16} className="text-accentBlue shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
          className="px-5 pb-5 text-sm text-gray-400 leading-relaxed border-t border-white/8 pt-4">
          {a}
        </motion.div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ContactPage = () => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.email.trim())     e.email     = 'Required';
    if (!form.subject)          e.subject   = 'Please select a subject';
    if (!form.message.trim())   e.message   = 'Please enter your message';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setErrors({});
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);
    setSuccess(true);
    setForm({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
  };

  const inputCls = (field) =>
    `w-full bg-white/5 border ${errors[field] ? 'border-red-500/50' : 'border-white/10'} p-3.5 rounded-xl text-sm text-white placeholder:text-gray-500 outline-none focus:border-accentBlue focus:ring-2 focus:ring-accentBlue/15 transition-all`;

  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-accentBlue/6 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-60 h-60 bg-purple-500/6 rounded-full blur-[80px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-accentBlue/10 border border-accentBlue/20 rounded-full text-accentBlue text-xs font-bold uppercase tracking-wider mb-6">
            <MessageSquare size={12} /> Contact Us
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl font-extrabold text-white tracking-tight mb-4">
            We'd love to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accentBlue to-blue-300">hear from you</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-lg max-w-xl mx-auto">
            Have a question, suggestion or need help? Reach out — our team usually responds within 24 hours.
          </motion.p>
        </div>
      </section>

      {/* ── Contact Info Cards ── */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CONTACT_INFO.map(({ icon: Icon, label, value, sub, color, bg, border, href }) => {
            const Card = (
              <div className={`${bg} border ${border} rounded-2xl p-5 hover:-translate-y-1 transition-all duration-300 ${href ? 'cursor-pointer' : ''}`}>
                <div className={`w-10 h-10 rounded-xl ${bg} border ${border} flex items-center justify-center mb-4`}>
                  <Icon size={18} className={color} />
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
                <p className="font-bold text-white text-sm">{value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </div>
            );
            return (
              <motion.div key={label} variants={fadeUp}>
                {href ? <a href={href} target="_blank" rel="noopener noreferrer">{Card}</a> : Card}
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* ── Form + FAQ ── */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="lg:col-span-3">
            <div className="bg-white/3 border border-white/10 rounded-3xl p-8">
              <h2 className="text-2xl font-extrabold text-white mb-1">Send a Message</h2>
              <p className="text-gray-400 text-sm mb-8">Fill in the form below and we'll get back to you shortly.</p>

              {success ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                  className="flex flex-col items-center py-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mb-6">
                    <CheckCircle size={40} className="text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-gray-400 text-sm max-w-xs">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button onClick={() => setSuccess(false)}
                    className="mt-8 px-6 py-2.5 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue/15 transition-colors">
                    Send Another
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                        placeholder="First Name" className={inputCls('firstName')} />
                      {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <input value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                        placeholder="Last Name" className={inputCls('lastName')} />
                    </div>
                  </div>

                  {/* Email + Phone row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                        placeholder="Email Address" className={inputCls('email')} />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <input type="tel" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="Phone (optional)" className={inputCls('phone')} />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <select value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                      className={inputCls('subject')}
                      style={{ backgroundColor: '#0b0e14', color: form.subject ? '#ffffff' : '#6b7280' }}>
                      <option value="" style={{ backgroundColor: '#0b0e14', color: '#6b7280' }}>Select a Subject</option>
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s} style={{ backgroundColor: '#0b0e14', color: '#ffffff' }}>{s}</option>
                      ))}
                    </select>
                    {errors.subject && <p className="text-red-400 text-xs mt-1">{errors.subject}</p>}
                  </div>

                  {/* Message */}
                  <div>
                    <textarea value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                      placeholder="Write your message here…" rows={5}
                      className={`${inputCls('message')} resize-none`} />
                    {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                  </div>

                  <button type="submit" disabled={loading}
                    className="w-full py-4 bg-accentBlue text-black font-bold rounded-xl flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100 shadow-lg shadow-accentBlue/20 text-sm">
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      <><Send size={16} /> Send Message</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* FAQ */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6">

            <div>
              <h2 className="text-2xl font-extrabold text-white mb-1">Frequently Asked</h2>
              <p className="text-gray-400 text-sm mb-6">Quick answers to common questions.</p>
              <div className="space-y-3">
                {FAQS.map((faq) => <FaqItem key={faq.q} {...faq} />)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
